import { useState, type FormEvent, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageBreadcrumb } from '@/components/ui/PageBreadcrumb'
import { httpService } from '@/services/httpService'
import type { ApiError } from '@/types/api'

const API_BASE = import.meta.env.VITE_API_URL ?? '/api/v1'

type Step = 'details' | 'otp' | 'report'

const initial = {
  receiptNumber: '',
  mobile: '',
  otp: '',
}

const STEPS: { key: Step; label: string; icon: string }[] = [
  { key: 'details', label: 'Your details', icon: 'fa-solid fa-id-card' },
  { key: 'otp', label: 'Verify OTP', icon: 'fa-solid fa-shield-halved' },
  { key: 'report', label: 'View report', icon: 'fa-solid fa-file-medical' },
]

/** Small helper line rendered under a field to show what to type. */
function FieldHint({ id, children }: { id: string; children: ReactNode }) {
  return (
    <small
      id={id}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 6,
        marginTop: 4,
        color: '#6b7280',
        fontSize: 13,
        lineHeight: 1.4,
      }}
    >
      <i className="fa-solid fa-circle-info" style={{ marginTop: 3, color: '#94a3b8' }} aria-hidden="true"></i>
      <span>{children}</span>
    </small>
  )
}

export function ReportCheck() {
  const [searchParams] = useSearchParams()
  // Prefill the receipt number when the page is opened by scanning a receipt QR
  // code, e.g. https://fastaid.com.bd/report-check?receipt=DR-2026-000023.
  const presetReceipt = (
    searchParams.get('receipt') ??
    searchParams.get('receiptNumber') ??
    ''
  ).trim()
  const [step, setStep] = useState<Step>('details')
  const [form, setForm] = useState(() => ({ ...initial, receiptNumber: presetReceipt }))
  const [devOtp, setDevOtp] = useState('')
  const [token, setToken] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const pdfUrl = token
    ? `${API_BASE}/portal/report-check/report.pdf?token=${encodeURIComponent(token)}`
    : ''

  const activeIndex = STEPS.findIndex((s) => s.key === step)

  const update = (field: keyof typeof initial, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleError = (err: unknown) => {
    const apiError = err as ApiError
    setError(apiError?.message ?? 'Something went wrong. Please try again.')
  }

  const sendOtp = async (): Promise<boolean> => {
    setError('')
    setLoading(true)
    try {
      const res = await httpService.post('/portal/report-check/send-otp', {
        mobile: form.mobile.trim(),
        receiptNumber: form.receiptNumber.trim(),
      })
      setDevOtp(res.data?.data?.otp ?? '')
      setForm((prev) => ({ ...prev, otp: '' }))
      setStep('otp')
      return true
    } catch (err) {
      handleError(err)
      return false
    } finally {
      setLoading(false)
    }
  }

  const handleSendOtp = async (event: FormEvent) => {
    event.preventDefault()
    await sendOtp()
  }

  const handleResendOtp = async () => {
    await sendOtp()
  }

  const handleVerifyOtp = async (event: FormEvent) => {
    event.preventDefault()
    setError('')
    setLoading(true)
    try {
      const res = await httpService.post('/portal/report-check/verify-otp', {
        mobile: form.mobile.trim(),
        receiptNumber: form.receiptNumber.trim(),
        otp: form.otp.trim(),
      })
      setToken(res.data?.data?.token ?? '')
      setStep('report')
    } catch (err) {
      handleError(err)
    } finally {
      setLoading(false)
    }
  }

  const reset = () => {
    setForm({ ...initial, receiptNumber: presetReceipt })
    setDevOtp('')
    setToken('')
    setError('')
    setStep('details')
  }

  const goToDetails = () => {
    setForm((prev) => ({ ...prev, otp: '' }))
    setDevOtp('')
    setError('')
    setStep('details')
  }

  return (
    <>
      <PageBreadcrumb title="Report Check" hideContent compact />

      <section className="report-check pt-60 md-pt-40 pb-100 md-pb-80">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-7 col-md-10">
              <h1 className="visually-hidden">Check &amp; Download Your Patient Report</h1>
              <div className="section-title text-center">
                <span className="sub-title">Patient Report</span>
                <h2>Check &amp; Download Your Report</h2>
                <p style={{ color: '#6b7280' }}>
                  Three quick steps and your report is ready to view or download.
                </p>
              </div>

              {/* Step progress */}
              <ol
                className="list-unstyled d-flex justify-content-center align-items-center flex-wrap"
                style={{ gap: 8, margin: '14px 0 14px' }}
              >
                {STEPS.map((s, index) => {
                  const isDone = index < activeIndex
                  const isActive = index === activeIndex
                  return (
                    <li key={s.key} className="d-flex align-items-center" style={{ gap: 8 }}>
                      <div className="d-flex align-items-center" style={{ gap: 10 }}>
                        <span
                          style={{
                            width: 38,
                            height: 38,
                            borderRadius: '50%',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 600,
                            fontSize: 15,
                            border: '1px solid',
                            borderColor: isDone || isActive ? 'var(--primary-color)' : '#e5e7eb',
                            background: isDone || isActive ? 'var(--primary-color)' : '#fff',
                            color: isDone || isActive ? '#fff' : '#9ca3af',
                            transition: 'all .2s ease',
                          }}
                          aria-hidden="true"
                        >
                          {isDone ? <i className="fa-solid fa-check"></i> : <i className={s.icon}></i>}
                        </span>
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: isActive ? 600 : 500,
                            color: isActive ? '#111827' : '#6b7280',
                          }}
                        >
                          {s.label}
                        </span>
                      </div>
                      {index < STEPS.length - 1 && (
                        <span
                          style={{
                            width: 28,
                            height: 2,
                            background: index < activeIndex ? 'var(--primary-color)' : '#e5e7eb',
                            borderRadius: 2,
                          }}
                          aria-hidden="true"
                        />
                      )}
                    </li>
                  )
                })}
              </ol>

              <div
                className="default-form mt-2"
                style={{
                  background: '#fff',
                  borderRadius: 20,
                  padding: 24,
                  boxShadow: '0 0 20px 0 rgba(0, 0, 0, 0.05)',
                }}
              >
                {error && (
                  <div
                    role="alert"
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                      background: '#fdecec',
                      color: '#b42318',
                      border: '1px solid #f5c2c0',
                      borderRadius: 10,
                      padding: '12px 16px',
                      marginBottom: 16,
                    }}
                  >
                    <i className="fa-solid fa-circle-exclamation" style={{ marginTop: 3 }} aria-hidden="true"></i>
                    <span>{error}</span>
                  </div>
                )}

                {step === 'details' && (
                  <form onSubmit={handleSendOtp}>
                    <p style={{ marginBottom: 12, color: '#525252' }}>
                      Enter the details from your diagnostic receipt and the mobile number registered with the
                      hospital. We&apos;ll text you a one-time password (OTP) to confirm it&apos;s you.
                    </p>

                    <div
                      style={{
                        background: '#f6f9fc',
                        border: '1px solid #e3edf5',
                        borderRadius: 12,
                        padding: '12px 16px',
                        marginBottom: 16,
                      }}
                    >
                      <strong style={{ display: 'block', marginBottom: 4, color: '#1f2937' }}>
                        <i className="fa-solid fa-lightbulb me-2" style={{ color: '#f59e0b' }} aria-hidden="true"></i>
                        Where do I find these?
                      </strong>
                      <ul style={{ margin: 0, paddingLeft: 20, color: '#4b5563', fontSize: 14, lineHeight: 1.5 }}>
                        <li>The <strong>receipt number</strong> is printed at the top of your diagnostic receipt.</li>
                        <li>Use the <strong>mobile number</strong> you gave when the receipt was created.</li>
                      </ul>
                    </div>

                    <div className="row">
                      <div className="col-12">
                        <div className="form-group">
                          <div className="form-floating field-inner">
                            <input
                              id="receiptNumber"
                              className="form-control"
                              name="receiptNumber"
                              type="text"
                              placeholder="Ex. DR-2026-000001"
                              autoComplete="off"
                              aria-describedby="receiptNumber-hint"
                              value={form.receiptNumber}
                              onChange={(e) => update('receiptNumber', e.target.value)}
                              required
                            />
                            <label htmlFor="receiptNumber">Receipt Number</label>
                          </div>
                          <FieldHint id="receiptNumber-hint">
                            Example: <strong>DR-2026-000001</strong>
                          </FieldHint>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="form-group">
                          <div className="form-floating field-inner">
                            <input
                              id="mobile"
                              className="form-control"
                              name="mobile"
                              type="tel"
                              placeholder="Ex. +8801XXXXXXXXX"
                              autoComplete="off"
                              aria-describedby="mobile-hint"
                              value={form.mobile}
                              onChange={(e) => update('mobile', e.target.value)}
                              required
                            />
                            <label htmlFor="mobile">Registered Mobile Number</label>
                          </div>
                          <FieldHint id="mobile-hint">
                            Include the country code, e.g. <strong>+880 1712 345678</strong>
                          </FieldHint>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="availability-btn-wapper mt-10">
                          <button
                            type="submit"
                            className="theme-button style-1 w-100 justify-content-center"
                            data-text="Send OTP"
                            disabled={loading}
                          >
                            <span data-text="Send OTP">{loading ? 'Sending...' : 'Send OTP'}</span>
                            <i className="fa-solid fa-arrow-right"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                )}

                {step === 'otp' && (
                  <form onSubmit={handleVerifyOtp}>
                    <p style={{ marginBottom: 8, color: '#525252' }}>
                      We sent a 6-digit code to <strong>{form.mobile}</strong>
                    </p>
                    <p style={{ marginBottom: 14, color: '#6b7280', fontSize: 14 }}>
                      for receipt <strong>{form.receiptNumber}</strong>. It expires in a few minutes.
                    </p>

                    {devOtp && (
                      <div
                        style={{
                          display: 'flex',
                          gap: 10,
                          alignItems: 'flex-start',
                          background: '#eef6ff',
                          border: '1px solid #cfe3fb',
                          borderRadius: 10,
                          padding: '12px 16px',
                          marginBottom: 14,
                          color: '#1d4ed8',
                          fontSize: 14,
                        }}
                      >
                        <i className="fa-solid fa-flask" style={{ marginTop: 3 }} aria-hidden="true"></i>
                        <span>
                          Demo mode — your OTP is <strong>{devOtp}</strong>
                        </span>
                      </div>
                    )}

                    <div className="row">
                      <div className="col-12">
                        <div className="form-group">
                          <div className="form-floating field-inner">
                            <input
                              id="otp"
                              className="form-control"
                              name="otp"
                              type="text"
                              inputMode="numeric"
                              maxLength={6}
                              placeholder="6-digit code"
                              autoComplete="one-time-code"
                              aria-describedby="otp-hint"
                              style={{ letterSpacing: 6, fontWeight: 600, textAlign: 'center' }}
                              value={form.otp}
                              onChange={(e) => update('otp', e.target.value.replace(/\D/g, '').slice(0, 6))}
                              required
                            />
                            <label htmlFor="otp">Enter 6-digit OTP</label>
                          </div>
                          <FieldHint id="otp-hint">
                            Type the 6 digits exactly as received. Didn&apos;t get it? Check your SMS or tap{' '}
                            <strong>Resend code</strong> below.
                          </FieldHint>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="availability-btn-wapper mt-10 d-flex flex-wrap align-items-center gap-3">
                          <button
                            type="submit"
                            className="theme-button style-1"
                            data-text="Verify & View Report"
                            disabled={loading || form.otp.length !== 6}
                          >
                            <span data-text="Verify & View Report">
                              {loading ? 'Verifying...' : 'Verify & View Report'}
                            </span>
                            <i className="fa-solid fa-arrow-right"></i>
                          </button>
                          <button
                            type="button"
                            onClick={handleResendOtp}
                            className="theme-button style-2"
                            data-text="Resend code"
                            disabled={loading}
                          >
                            <span data-text="Resend code">Resend code</span>
                            <i className="fa-solid fa-rotate-right"></i>
                          </button>
                          <button
                            type="button"
                            onClick={goToDetails}
                            className="btn btn-link p-0"
                            style={{ color: '#6b7280', textDecoration: 'none' }}
                          >
                            <i className="fa-solid fa-arrow-left me-1" aria-hidden="true"></i>
                            Change details
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                )}

                {step === 'report' && (
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        gap: 10,
                        alignItems: 'flex-start',
                        background: '#eefbf3',
                        border: '1px solid #c7ecd8',
                        borderRadius: 10,
                        padding: '12px 16px',
                        marginBottom: 14,
                        color: '#067647',
                      }}
                    >
                      <i className="fa-solid fa-circle-check" style={{ marginTop: 3 }} aria-hidden="true"></i>
                      <span>
                        Report for receipt <strong>{form.receiptNumber}</strong> is ready.
                      </span>
                    </div>
                    <iframe
                      src={pdfUrl}
                      title="Patient report"
                      style={{
                        width: '100%',
                        height: 600,
                        border: '1px solid #e5e7eb',
                        borderRadius: 12,
                        marginBottom: 14,
                      }}
                    ></iframe>
                    <div className="d-flex flex-wrap align-items-center gap-3">
                      <a
                        href={pdfUrl}
                        download={`report-${form.receiptNumber}.pdf`}
                        className="theme-button style-1"
                        data-text="Download Report"
                      >
                        <span data-text="Download Report">Download Report</span>
                        <i className="fa-solid fa-download"></i>
                      </a>
                      <button type="button" onClick={reset} className="theme-button style-2" data-text="New Search">
                        <span data-text="New Search">New Search</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
